import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { walletAPI } from '../../services/api';
import Sidebar from '../../components/Common/Sidebar';
import {
  Wallet, Plus, ArrowDownLeft, ArrowUpRight,
  TrendingUp, Clock, X, Loader2, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';

/* ── design tokens ── */
const C = {
  bg:          '#f4f4f9',
  surface:     '#FFFFFF',
  border:      '#e8e8f1',
  borderSoft:  '#f0f0f6',
  ink:         '#14142b',
  body:        '#4a4a6a',
  muted:       '#6e6e86',
  primary:     '#7c3aed',
  primarySoft: '#f1ebff',
  primaryTint: '#ddd6fe',
  success:     '#10B981',
  successSoft: '#D1FAE5',
  warning:     '#F59E0B',
  warnSoft:    '#FEF3C7',
  danger:      '#EF4444',
  dangerSoft:  '#FEE2E2',
  radius:      14,
  radiusSm:    8,
};

const fmtINR = (n) =>
  '₹ ' + Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const fmtDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

/* ── stat card ── */
const StatCard = ({ icon: Icon, iconColor, iconBg, label, value, loading }) => (
  <div style={{
    background: C.surface, border: `1.5px solid ${C.border}`,
    borderRadius: C.radius, padding: '18px 20px',
    display: 'flex', flexDirection: 'column', gap: 12,
    boxShadow: '0 1px 3px rgba(20,20,43,.04)',
  }}>
    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
      <div>
        <p style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.06em', margin: '0 0 6px' }}>
          {label}
        </p>
        <p style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0, letterSpacing: '-0.01em', lineHeight: 1, minHeight: 26, display: 'flex', alignItems: 'center' }}>
          {loading ? <span style={{ color: C.borderSoft }}>——</span> : value}
        </p>
      </div>
      <div style={{
        width: 38, height: 38, borderRadius: 10,
        background: iconBg,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={17} color={iconColor} strokeWidth={1.8} />
      </div>
    </div>
  </div>
);

/* ── status badge ── */
const StatusBadge = ({ status }) => {
  const map = {
    success: { bg: C.successSoft, color: '#065F46' },
    pending: { bg: C.warnSoft,    color: '#92400E' },
    failed:  { bg: C.dangerSoft,  color: '#991B1B' },
  };
  const s = map[status?.toLowerCase()] || map.pending;
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, padding: '3px 10px',
      borderRadius: 20, background: s.bg, color: s.color,
    }}>
      {(status || 'Pending').charAt(0).toUpperCase() + (status || 'pending').slice(1)}
    </span>
  );
};

const QUICK = [500, 1000, 2000, 5000, 10000];

const UserWallet = () => {
  const { user } = useAuth();
  const [loading,     setLoading]     = useState(true);
  const [walletData,  setWalletData]  = useState(null);
  const [showModal,   setShowModal]   = useState(false);
  const [amount,      setAmount]      = useState('');
  const [processing,  setProcessing]  = useState(false);
  const [amtFocus,    setAmtFocus]    = useState(false);

  useEffect(() => { fetchWallet(); }, []);

  const fetchWallet = async () => {
    try {
      const res = await walletAPI.getBalance();
      setWalletData(res.data.data);
    } catch {
      toast.error('Failed to load wallet data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMoney = async (e) => {
    e.preventDefault();
    const num = parseFloat(amount);
    if (isNaN(num) || num < 100)   { toast.error('Minimum amount is ₹100');     return; }
    if (num > 50000)               { toast.error('Maximum amount is ₹50,000');  return; }
    setProcessing(true);
    try {
      await walletAPI.addMoney({ amount: num });
      toast.success('Payment initiated! Complete the payment to credit your wallet.');
      setShowModal(false);
      setAmount('');
      await fetchWallet();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <div className="dashboard-layout">
      <Sidebar role="user" />

      <div className="dashboard-content" style={{ background: C.bg, minHeight: '100vh', padding: '28px 28px 40px' }}>

        {/* ── page header ── */}
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28, flexWrap: 'wrap', gap: 12 }}>
          <div>
            <h2 style={{ fontSize: 22, fontWeight: 800, color: C.ink, margin: 0 }}>My Wallet</h2>
            <p  style={{ fontSize: 13, color: C.muted, marginTop: 4 }}>Manage your balance and transactions</p>
          </div>
          <button
            onClick={() => setShowModal(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 7,
              padding: '9px 18px', borderRadius: C.radiusSm,
              background: C.primary, color: '#fff',
              fontWeight: 700, fontSize: 13, border: 'none', cursor: 'pointer',
            }}
          >
            <Plus size={14} strokeWidth={2.5} /> Add Money
          </button>
        </div>

        {loading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '80px 0' }}>
            <Loader2 size={32} color={C.primary} style={{ animation: 'spin 1s linear infinite' }} />
          </div>
        ) : (
          <>
            {/* ── balance hero card ── */}
            <div style={{
              background: `linear-gradient(135deg, ${C.primary} 0%, #6d28d9 100%)`,
              borderRadius: C.radius, padding: '28px 32px', marginBottom: 22,
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              flexWrap: 'wrap', gap: 20,
              boxShadow: '0 4px 20px rgba(124,58,237,.28)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
                <div style={{
                  width: 56, height: 56, borderRadius: 16,
                  background: 'rgba(255,255,255,.18)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Wallet size={28} color="#fff" strokeWidth={1.6} />
                </div>
                <div>
                  <p style={{ fontSize: 12, color: 'rgba(255,255,255,.7)', fontWeight: 600, margin: 0, letterSpacing: '.04em' }}>
                    AVAILABLE BALANCE
                  </p>
                  <p style={{ fontSize: 34, fontWeight: 800, color: '#fff', margin: '6px 0 0', letterSpacing: '-0.02em' }}>
                    {fmtINR(walletData?.wallet_balance)}
                  </p>
                </div>
              </div>
              <button
                onClick={() => setShowModal(true)}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 7,
                  padding: '11px 22px', borderRadius: C.radiusSm,
                  background: 'rgba(255,255,255,.18)', color: '#fff',
                  fontWeight: 700, fontSize: 13, border: '1.5px solid rgba(255,255,255,.35)',
                  cursor: 'pointer', backdropFilter: 'blur(4px)',
                }}
              >
                <Plus size={14} strokeWidth={2.5} /> Add Money
              </button>
            </div>

            {/* ── stat cards ── */}
            <div className="wallet-stat-grid">
              <StatCard icon={ArrowDownLeft} iconColor="#065F46" iconBg={C.successSoft}
                label="Total Deposits"     value={fmtINR(walletData?.total_deposits)}     loading={loading} />
              <StatCard icon={ArrowUpRight}  iconColor="#991B1B" iconBg={C.dangerSoft}
                label="Total Withdrawals"  value={fmtINR(walletData?.total_withdrawals)}  loading={loading} />
              <StatCard icon={TrendingUp}    iconColor={C.primary}  iconBg={C.primarySoft}
                label="Total Commissions"  value={fmtINR(walletData?.total_commissions)}  loading={loading} />
            </div>

            {/* ── recent transactions ── */}
            <div style={{
              background: C.surface, border: `1px solid ${C.border}`,
              borderRadius: C.radius, padding: '22px 24px',
              boxShadow: '0 1px 3px rgba(20,20,43,.04)',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 18 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <Clock size={15} color={C.primary} strokeWidth={2} />
                  <span style={{ fontSize: 13, fontWeight: 700, color: C.ink }}>Recent Transactions</span>
                </div>
                <Link to="/transactions" style={{
                  display: 'inline-flex', alignItems: 'center', gap: 4,
                  fontSize: 12, fontWeight: 600, color: C.primary, textDecoration: 'none',
                }}>
                  View All <ArrowRight size={12} />
                </Link>
              </div>

              {walletData?.recent_transactions?.length > 0 ? (
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <thead>
                      <tr style={{ borderBottom: `2px solid ${C.borderSoft}` }}>
                        {['Transaction ID', 'Type', 'Amount', 'Status', 'Date'].map(h => (
                          <th key={h} style={{
                            padding: '8px 12px', textAlign: 'left',
                            fontSize: 11, fontWeight: 700, color: C.muted,
                            textTransform: 'uppercase', letterSpacing: '.06em',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {walletData.recent_transactions.map((txn) => {
                        const typeIcon = txn.transaction_type === 'deposit'    ? <ArrowDownLeft size={13} color={C.success} />
                                       : txn.transaction_type === 'withdrawal' ? <ArrowUpRight  size={13} color={C.danger}  />
                                       : <TrendingUp size={13} color={C.primary} />;
                        return (
                          <tr key={txn.id} style={{ borderBottom: `1px solid ${C.borderSoft}` }}
                            onMouseEnter={e => e.currentTarget.style.background = C.bg}
                            onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                          >
                            <td style={{ padding: '11px 12px', fontFamily: 'monospace', fontSize: 12, color: C.body }}>
                              {txn.transaction_id}
                            </td>
                            <td style={{ padding: '11px 12px' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 6, textTransform: 'capitalize', color: C.body }}>
                                {typeIcon} {txn.transaction_type}
                              </div>
                            </td>
                            <td style={{ padding: '11px 12px', fontWeight: 700, color: C.ink }}>
                              {fmtINR(txn.amount)}
                            </td>
                            <td style={{ padding: '11px 12px' }}>
                              <StatusBadge status={txn.status} />
                            </td>
                            <td style={{ padding: '11px 12px', color: C.muted, fontSize: 12 }}>
                              {fmtDate(txn.created_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div style={{ textAlign: 'center', padding: '40px 0', color: C.muted, fontSize: 13 }}>
                  No recent transactions
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Add Money Modal ── */}
      {showModal && (
        <div
          onClick={() => !processing && setShowModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(20,20,43,.45)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, backdropFilter: 'blur(3px)',
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: C.surface, borderRadius: C.radius,
              width: '100%', maxWidth: 420, padding: '28px 28px 24px',
              boxShadow: '0 20px 60px rgba(20,20,43,.18)',
              position: 'relative',
            }}
          >
            {/* header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 22 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: C.primarySoft, color: C.primary,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <Wallet size={17} strokeWidth={1.8} />
                </div>
                <div>
                  <p style={{ margin: 0, fontSize: 15, fontWeight: 700, color: C.ink }}>Add Money</p>
                  <p style={{ margin: 0, fontSize: 11, color: C.muted }}>Min ₹100 · Max ₹50,000</p>
                </div>
              </div>
              <button onClick={() => !processing && setShowModal(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.muted, display: 'flex' }}>
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleAddMoney}>
              {/* amount input */}
              <div style={{ marginBottom: 16 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: C.body, marginBottom: 6, display: 'block' }}>
                  Enter Amount
                </label>
                <div style={{
                  display: 'flex', alignItems: 'center',
                  border: `1.5px solid ${amtFocus ? C.primary : C.border}`,
                  borderRadius: C.radiusSm, overflow: 'hidden',
                  background: C.bg,
                }}>
                  <span style={{ padding: '0 12px', fontSize: 16, fontWeight: 700, color: C.muted }}>₹</span>
                  <input
                    type="number" value={amount}
                    onChange={e => setAmount(e.target.value)}
                    onFocus={() => setAmtFocus(true)}
                    onBlur={() => setAmtFocus(false)}
                    placeholder="0.00" min="100" max="50000" required
                    style={{
                      flex: 1, padding: '10px 12px 10px 0',
                      fontSize: 15, fontWeight: 700, color: C.ink,
                      background: 'transparent', border: 'none', outline: 'none',
                    }}
                  />
                </div>
              </div>

              {/* quick amounts */}
              <div style={{ marginBottom: 18 }}>
                <label style={{ fontSize: 11, fontWeight: 600, color: C.muted, marginBottom: 8, display: 'block', textTransform: 'uppercase', letterSpacing: '.06em' }}>
                  Quick Select
                </label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {QUICK.map(amt => (
                    <button
                      key={amt} type="button"
                      onClick={() => setAmount(String(amt))}
                      style={{
                        padding: '6px 14px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                        cursor: 'pointer', transition: 'all .12s',
                        background: String(amount) === String(amt) ? C.primary : C.bg,
                        color:      String(amount) === String(amt) ? '#fff'    : C.body,
                        border: `1.5px solid ${String(amount) === String(amt) ? C.primary : C.border}`,
                      }}
                    >
                      ₹{amt.toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>
              </div>

              {/* note */}
              <div style={{
                background: C.primarySoft, border: `1px solid ${C.primaryTint}`,
                borderRadius: C.radiusSm, padding: '10px 14px', marginBottom: 20,
                fontSize: 12, color: C.body, lineHeight: 1.5,
              }}>
                <strong style={{ color: C.primary }}>Note:</strong> Your balance will be credited instantly after successful payment.
              </div>

              {/* actions */}
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button" disabled={processing}
                  onClick={() => setShowModal(false)}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                    background: C.bg, color: C.body,
                    border: `1px solid ${C.border}`, fontWeight: 600, fontSize: 13, cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit" disabled={processing}
                  style={{
                    flex: 1, padding: '10px 0', borderRadius: C.radiusSm,
                    background: processing ? C.muted : C.primary,
                    color: '#fff', border: 'none',
                    fontWeight: 700, fontSize: 13, cursor: processing ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
                  }}
                >
                  {processing
                    ? <><Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> Processing…</>
                    : <><Plus size={14} strokeWidth={2.5} /> Proceed to Pay</>
                  }
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin{to{transform:rotate(360deg)}}
        .wallet-stat-grid {
          display: grid;
          grid-template-columns: repeat(3,1fr);
          gap: 18px;
          margin-bottom: 24px;
        }
        @media (max-width: 600px) {
          .wallet-stat-grid { grid-template-columns: 1fr; }
        }
      `}</style>
    </div>
  );
};

export default UserWallet;
